/* eslint-disable import/no-unresolved */
import { KlevuEvents } from 'https://cdn.skypack.dev/pin/@klevu/core@v1.1.5-0q1n6dBIy9DgOZifPmpX/mode=imports,min/optimized/@klevu/core.js'
/* eslint-enable import/no-unresolved */

const { productCardCache } = window.BAO.Caches
const { translateString } = window.BAO.Utils
const { SearchResultsQuery } = window.BAO.Search.components

class SearchResultsLanding extends SearchResultsQuery {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.els['load-more'].element, 'click', () =>
      this.requestNextPage(),
    )

    this.els['sort-input'].elements.forEach(sortInput => {
      this.listeners.add(sortInput, 'input', event =>
        this.handleSortInputUpdate(event),
      )
    })

    this.listeners.add(this.els['sort-select'].element, 'input', event =>
      this.handleSortSelectUpdate(event),
    )

    this.listeners.add(this.els['facet-form'].element, 'input', event =>
      this.handleFacetFormSubmit(event),
    )
  }

  /** @param {SubmitEvent} event */
  handleFacetFormSubmit (event) {
    event.preventDefault()

    this.applyFilters()

    this.requestRefresh()
  }

  /** @param {InputEvent} event */
  handleSortInputUpdate (event) {
    const currentSortValue = this.sortMethod

    if (event.target.value === currentSortValue) {
      return
    }

    this.setAttribute('sort', event.target.value)

    this.requestRefresh()
  }

  /** @param {InputEvent} event */
  handleSortSelectUpdate (event) {
    const currentSortValue = this.sortMethod

    if (event.target.value === currentSortValue) {
      return
    }

    this.setAttribute('sort', event.target.value)

    this.requestRefresh()
  }

  applyFilters () {
    const formData = new FormData(this.els['facet-form'].element)

    this.filterManager.options.forEach(filter => {
      const selectedValuesInForm = formData.getAll(filter.key)
      filter.options.forEach(option => {
        const isSelectedInForm = selectedValuesInForm.includes(option.value)
        const valueSelectedInFormButNotInManager =
          option.selected && !isSelectedInForm
        const valueNotInFormButSelectedInManager =
          !option.selected && isSelectedInForm

        if (
          valueSelectedInFormButNotInManager ||
          valueNotInFormButSelectedInManager
        ) {
          this.filterManager.toggleOption(filter.key, option.value)
        }
      })
    })

    this.filterManager.sliders.forEach(filter => {
      this.filterManager.updateSlide(filter.key, ...formData.getAll(filter.key))
    })
  }

  async render (query, fallbackQuery) {
    this.skippedCount = 0
    this.renderHeader(query)
    this.renderFacets(query)
    await this.renderProducts(query, fallbackQuery)
    if (this.skippedCount > 0) {
      this.renderHeader(query)
    }
    this.renderPagination(query)
    this.renderSorting(query)
  }

  renderHeader (query) {
    const title = this.els['header-title'].element
    const subtitle = this.els['header-subtitle'].element

    const totalResults = query.records.length - (this.skippedCount || 0)
    const hasResults = totalResults > 0

    const terms = query.meta.searchedTerm

    title.textContent = hasResults
      ? this.dataset.headerTitleWithResultsTemplate
      : translateString(this.dataset.headerTitleNoResultsTemplate, { terms })

    let subtitleTemplate = this.dataset.headerSubtitleWithResultsPluralTemplate
    if (totalResults === 1) {
      subtitleTemplate = this.dataset.headerSubtitleWithResultsSingularTemplate
    } else if (totalResults === 0) {
      subtitleTemplate = this.dataset.headerSubtitleNoResultsTemplate
    }

    subtitle.textContent = translateString(subtitleTemplate, {
      count: totalResults,
      terms,
    })
  }

  renderFacets ({ filters }) {
    /** @return {HTMLElement[]} */
    const getFacets = () => [...this.els.facets.element.children]

    const openFilterId = (() => {
      const existingFacets = getFacets()

      const openFacet = existingFacets.find(facet => facet.hasAttribute('open'))

      if (!openFacet) {
        return
      }

      return openFacet.dataset.accordionId
    })()

    const facets = filters.map((filter, index) => {
      if (filter.key === 'klevu_price' && Shopify.currency.active !== 'GBP') {
        return ``
      }

      const facetAccordionItem = this.els[
        'facet-template'
      ].element.content.cloneNode(true)
      const facetAccordion = facetAccordionItem.children[0]
      const facetToggle = facetAccordion.querySelector(
        '[data-search-results-landing-el="facet-toggle"]',
      )

      facetAccordion.dataset.accordionId = filter.key

      /** @note For some reason Klevu returns price range filter label as 'klevu_price' */
      const label =
        filter.type === 'SLIDER'
          ? this.dataset.priceRangeFilterLabelTemplate
          : filter.label

      facetToggle.innerHTML = `${label} ${facetToggle.innerHTML}`
      facetToggle.dataset.accordionId = filter.label

      if (filter.type === 'OPTIONS') {
        const facetList = facetAccordion.querySelector(
          '[data-search-results-landing-el="facet-list"]',
        )

        facetList.dataset.index = index

        const options = this.renderOptionsFacet({ filter, index })

        facetList.replaceChildren(...options)
      } else {
        const facetPanel = facetAccordion.querySelector(
          '[data-search-results-landing-el="facet-panel"]',
        )
        const priceRange = this.renderPriceRangeFacet({ filter, index })

        facetPanel.replaceChildren(priceRange)
      }

      return facetAccordion
    })

    this.els.facets.element.replaceChildren(...facets)

    const openFacet = getFacets().find(
      facet => facet.dataset.accordionId === openFilterId,
    )
    if (openFacet) {
      openFacet.isOpen = true
    }
  }

  renderPriceRangeFacet ({ filter, index }) {
    const facetPriceRangeItem = this.els[
      'facet-price-range-template'
    ].element.content.cloneNode(true)
    const facetPriceRange = facetPriceRangeItem.children[0]
    const facetMinLabel = facetPriceRange.querySelector(
      '[data-search-results-landing-el="facet-price-range-min-label"]',
    )
    const facetMinInput = facetPriceRange.querySelector(
      '[data-search-results-landing-el="facet-price-range-min-input"]',
    )
    const facetMaxLabel = facetPriceRange.querySelector(
      '[data-search-results-landing-el="facet-price-range-max-label"]',
    )
    const facetMaxInput = facetPriceRange.querySelector(
      '[data-search-results-landing-el="facet-price-range-max-input"]',
    )

    const minId = `${filter.key}-gte`
    const maxId = `${filter.key}-lte`

    facetPriceRange.dataset.index = index

    facetMinLabel.setAttribute('for', minId)
    facetMinInput.id = minId
    facetMinInput.name = filter.key
    facetMinInput.max = Number(filter.max)
    facetMinInput.value = Number(filter.start || filter.min)

    facetMaxLabel.setAttribute('for', maxId)
    facetMaxInput.id = maxId
    facetMaxInput.name = filter.key
    facetMaxInput.max = Number(filter.max)
    facetMaxInput.placeholder = Number(filter.max)
    facetMaxInput.value = Number(filter.end || filter.max)

    return facetPriceRange
  }

  renderOptionsFacet ({ filter }) {
    return filter.options.map(option => {
      const facetOptionItem = this.els[
        'facet-option-template'
      ].element.content.cloneNode(true)
      const facetOptionInput = facetOptionItem.querySelector(
        '[data-search-results-landing-el="facet-option-input"]',
      )
      const facetOptionLabel = facetOptionItem.querySelector(
        '[data-search-results-landing-el="facet-option-label"]',
      )
      const facetOptionCheckbox = facetOptionItem.querySelector(
        '[data-search-results-landing-el="facet-option-checkbox"]',
      )

      const facetOptionId = `${filter.key}-${option.value}`

      facetOptionInput.checked = option.selected
      facetOptionInput.id = facetOptionId
      facetOptionInput.name = filter.key
      facetOptionInput.value = option.value

      facetOptionLabel.textContent = translateString(
        this.dataset.optionFilterValueTemplate,
        {
          value: option.name,
          count: option.count,
        },
      )
      facetOptionLabel.setAttribute('for', facetOptionId)

      const isColourInput = this.colours.filters.includes(filter.label)
      const colourSwatch = this.colours.swatches.find(
        swatch => swatch.name === option.value,
      )
      if (isColourInput && colourSwatch) {
        facetOptionCheckbox.classList.add(this.colours.checkboxClass)
        facetOptionCheckbox.style.setProperty(
          '--OptionCheckbox_Background',
          colourSwatch.colour,
        )
      }

      return facetOptionItem
    })
  }

  renderPagination ({ meta, next, records }) {
    const useFallbackQuery = records.length === 0

    this.els.pagination.element.setAttribute('aria-hidden', useFallbackQuery)

    const hasNextPage = Boolean(next)
    this.els['load-more'].element.setAttribute('aria-hidden', !hasNextPage)

    const skipped = this.skippedCount || 0
    const currentCount = records.length - skipped
    const totalCount = meta.totalResultsFound - skipped

    this.els['pagination-label'].element.textContent = translateString(
      this.dataset.paginationLabelTemplate,
      {
        current: currentCount,
        total: totalCount,
      },
    )

    const progressBar = this.els['progress-bar'].element
    progressBar.style.setProperty('--ProgressBar_Max', totalCount)
    progressBar.style.setProperty('--ProgressBar_Value', currentCount)
  }

  async renderProducts ({ meta, records }, { records: fallbackRecords } = {}) {
    const useFallbackQuery = records.length === 0

    this.els['list-header'].element.setAttribute(
      'aria-hidden',
      useFallbackQuery,
    )

    const recordsToRender = !useFallbackQuery ? records : fallbackRecords

    const productCards = await Promise.all(
      recordsToRender.map(record => {
        const handle = new URL(record.url).pathname.replace(/^\/products\//, '')

        return productCardCache.get(handle)
      }),
    )

    const filteredPairs = productCards
      .map((productCard, index) => ({
        productCard,
        record: recordsToRender[index],
      }))
      .filter(({ productCard }) => productCard && productCard.trim())

    // Fallback products aren't part of the query's result count
    this.skippedCount = useFallbackQuery
      ? 0
      : productCards.length - filteredPairs.length

    const listItems = filteredPairs.map(({ productCard, record }) => {
      const listItem = this.els['item-template'].element.content.cloneNode(true)
      const item = listItem.querySelector(
        '[data-search-results-landing-el="item"]',
      )

      item.innerHTML = productCard

      const card = item.querySelector('.prd-Card')
      card.addEventListener('click', event => {
        KlevuEvents.searchProductClick(record, meta.searchedTerm)
      })

      return listItem
    })

    this.els.list.element.replaceChildren(...listItems)

    window.yotpoWidgetsContainer.initWidgets(false)
  }

  renderSorting () {
    const { 'sort-input': sortInput, 'sort-select': sortSelect } = this.els

    if (sortSelect.element.value !== this.sortMethod) {
      sortSelect.element.value = this.sortMethod
    }

    const checkedInput = sortInput.elements.find(input => input.checked)
    const nextCheckedInput = sortInput.elements.find(
      input => input.value === this.sortMethod,
    )
    if (checkedInput !== nextCheckedInput) {
      nextCheckedInput.checked = true
    }
  }

  requestRefresh () {
    this.dispatchEvent(
      new CustomEvent('search-results:refresh', {
        bubbles: true,
        detail: { queryId: this.queryId },
      }),
    )
  }

  requestNextPage () {
    this.dispatchEvent(
      new CustomEvent('search-results:next', {
        bubbles: true,
        detail: { queryId: this.queryId },
      }),
    )
  }

  get colours () {
    return JSON.parse(this.dataset.colours)
  }
}

if (!customElements.get('search-results-landing')) {
  customElements.define('search-results-landing', SearchResultsLanding)
}
